import React, { Suspense, lazy, useEffect, useState } from "react";
import Hero from "../components/Hero";
import { WhatIsBhajanJamming, TrustBar, MeetAntara, FinalCTA } from "../components/Conversion";
import {
  WatchAntara,
  EmotionalArc,
  InstagramEmbed,
  Voices,
  WhyAttend,
  DelhiRecap,
  Gallery,
  AboutAntara,
  Curators,
  FAQ,
} from "../components/Sections";
import Origin from "../components/Origin";
import InterestForm from "../components/InterestForm";
import PerformBand from "../components/PerformBand";
import { MediaStrip } from "../components/Media";
import ReelTeaser from "../components/ReelTeaser";
import ShowReels from "../components/ShowReels";
import PosterSection from "../components/PosterSection";
import Seo from "../components/seo/Seo";
import JsonLd from "../components/seo/JsonLd";
import { ShowsHero, UpcomingShows } from "../components/Shows";
import { ROUTES, breadcrumbs, siteSchema } from "../lib/seo";
import { OG_POSTER } from "../lib/constants";
import { useUpcomingShows, soldOutShows, showsJsonLd, OG_SHOW_IMAGE } from "../lib/shows";
import NextSteps from "../components/NextSteps";

const GhatLayer = lazy(() => import("../components/three/GhatLayer"));

export default function Home() {
  // While a Bhaav Sangam date is ahead, the poster is the hero and the shows
  // sit straight under it. Once both have passed, the photograph hero comes
  // back, opening on the sold-out line while those editions stay up as proof.
  const shows = useUpcomingShows();
  const live = shows.length > 0;
  // Nothing on sale, but the editions that sold out stay up as proof, and the
  // page turns to what comes next: the three forms, one tap from the top.
  const soldOut = live ? [] : soldOutShows();
  const sold = soldOut.length > 0;
  const [enableGhat, setEnableGhat] = useState(false);
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Never during the react-snap snapshot: a layer switched on by an effect
    // would be baked into the static HTML but absent from the first client
    // render, and that hydration mismatch makes React re-render the page.
    setEnableGhat(!reduced && navigator.userAgent !== "ReactSnap");
  }, []);

  const interest = (
    <section className="bg-cream py-24 md:py-32 px-6">
      <InterestForm />
    </section>
  );

  return (
    <>
      {live ? (
        <Seo route={ROUTES.home} image={OG_SHOW_IMAGE.url}>
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content={String(OG_SHOW_IMAGE.width)} />
          <meta property="og:image:height" content={String(OG_SHOW_IMAGE.height)} />
          <meta property="og:image:alt" content={ROUTES.home.imageAlt} />
          <meta name="twitter:image:alt" content={ROUTES.home.imageAlt} />
        </Seo>
      ) : (
        <Seo route={ROUTES.homeDefault} image={OG_POSTER} />
      )}
      <JsonLd data={[...siteSchema(), ...showsJsonLd(live ? shows : soldOut), breadcrumbs([])]} />

      {live ? (
        <>
          <ShowsHero shows={shows} />
          <UpcomingShows shows={shows} />
          <ShowReels shows={shows} />
        </>
      ) : sold ? (
        <>
          <Hero soldOut={soldOut} />
          <NextSteps />
          {interest}
          <UpcomingShows shows={soldOut} />
          <ShowReels shows={[]} />
        </>
      ) : (
        <Hero />
      )}
      <TrustBar />
      <Origin />
      <WhatIsBhajanJamming />
      {!live && !sold && <PosterSection />}
      <WatchAntara />
      <InstagramEmbed />
      {/* The single-reel teaser steps aside while ShowReels is up. */}
      {!live && !sold && <ReelTeaser />}
      <Voices preview />
      <MediaStrip />
      <MeetAntara />
      <EmotionalArc />
      <WhyAttend />

      <section className="relative">
        {enableGhat && (
          <Suspense fallback={null}>
            <div className="absolute inset-0 z-0 pointer-events-none">
              <GhatLayer />
            </div>
          </Suspense>
        )}
        <div className="relative z-10">
          <DelhiRecap />
        </div>
      </section>

      {/* While tickets are on sale the open call and the interest list step
          out of the way: the open call is still linked from the footer, and
          the interest list (the /#interest anchor) moves to the very end, for
          the visitors neither city reaches. */}
      {!live && <PerformBand />}
      {!live && !sold && interest}

      <Gallery preview />
      <AboutAntara />
      <Curators />
      <FAQ preview />
      <FinalCTA />
      {live && interest}
    </>
  );
}
